import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { siteName } from "@/mocks/comics";

const navLinks = [
  { label: "首页", path: "/" },
  { label: "分类专区", path: "/category" },
  { label: "人气榜单", path: "/#ranking" },
  { label: "专题解读", path: "/#feature" },
  { label: "关于我们", path: "/about" },
  { label: "联系我们", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/category?q=${encodeURIComponent(q)}` : "/category");
  };

  const isActive = (path: string) => {
    if (path.startsWith("/#")) {
      const anchor = path.split("#")[1];
      return location.pathname === "/" && location.hash === `#${anchor}`;
    }
    return location.pathname === path;
  };

  const handleAnchor = (path: string) => {
    if (path.startsWith("/#")) {
      const anchor = path.slice(2);
      if (location.pathname === "/") {
        document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate(`/#${anchor}`);
      }
      return;
    }
    navigate(path);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "bg-background-50/95 backdrop-blur shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="flex items-center justify-between px-4 md:px-8 h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <span className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-heart-3-fill text-lg"></i>
          </span>
          <span
            className={`font-heading font-bold text-lg md:text-xl tracking-wide ${
              scrolled || open ? "text-primary-700" : "text-background-50"
            }`}
          >
            羞羞动漫漫画
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.path}
              type="button"
              onClick={() => handleAnchor(link.path)}
              className={`whitespace-nowrap text-sm font-medium transition-colors ${
                isActive(link.path)
                  ? "text-primary-600 font-semibold"
                  : scrolled || open
                    ? "text-foreground-700 hover:text-primary-600"
                    : "text-background-50/90 hover:text-background-50"
              }`}
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="hidden lg:block">
          <form onSubmit={handleSearch} className="flex items-center">
            <div className="flex items-center gap-2 rounded-full border px-3 py-2 bg-background-50/70 focus-within:border-primary-300">
              <i className="ri-search-line text-sm text-foreground-500"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画"
                className="w-36 bg-transparent text-sm outline-none placeholder:text-foreground-400"
              />
            </div>
          </form>
        </div>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setOpen(!open)}
          className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg ${
            scrolled || open ? "text-foreground-800" : "text-background-50"
          }`}
        >
          <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 px-4 pb-4">
          <form onSubmit={handleSearch} className="flex items-center gap-2 rounded-full border px-3 py-2 my-3 bg-background-100">
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画"
              className="w-full bg-transparent text-sm outline-none"
            />
          </form>
          <div className="flex flex-col">
            {navLinks.map((link) => (
              <button
                key={link.path}
                type="button"
                onClick={() => handleAnchor(link.path)}
                className={`text-left px-2 py-3 text-sm border-b border-background-100 last:border-0 ${
                  isActive(link.path) ? "text-primary-600 font-semibold" : "text-foreground-800"
                }`}
              >
                {link.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}